
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { OrgWithPlan } from "@/lib/orgBilling";
import { PlanKey } from "@/config/plans";

// Re-using the types from orgBilling but fetching here to control the query precisely
export type { OrgWithPlan }; // Re-export

export function useOrg() {
    const queryClient = useQueryClient();
    const isCreatingRef = useRef(false);
    const [createError, setCreateError] = useState<Error | null>(null);

    // 1. Get User
    const { data: user, isLoading: isUserLoading } = useQuery({
        queryKey: ["auth-user"],
        queryFn: async () => {
            const { data: { user } } = await supabase.auth.getUser();
            return user;
        },
    });

    // 2. Get Org (dependent on User)
    const {
        data: orgData,
        isLoading: isOrgLoading,
        error: orgError,
        refetch
    } = useQuery({
        queryKey: ["org-context", user?.id],
        enabled: !!user?.id,
        queryFn: async () => {
            // Resolve org via query on public.organization_members
            const { data, error } = await supabase
                .from("organization_members")
                .select(`
                    role,
                    organization:organizations (
                        id,
                        name,

                        logo_path,
                        plan_key,
                        plan_status,
                        stripe_customer_id,
                        stripe_subscription_id,
                        stripe_price_id,
                        current_period_end,
                        trial_end,
                        trial_end,
                        cancel_at_period_end,
                        sponsor_name,
                        sponsor_address,
                        support_email,
                        updated_at
                    )
                `)
                .eq("user_id", user!.id)
                .order("created_at", { ascending: false })
                .limit(1)
                .maybeSingle();

            if (error) {
                console.error("Error fetching org:", error);
                throw error;
            }

            if (data) {
                const membership = { role: data.role };

                // Normalization
                if (data.organization) {
                    const rawOrg = data.organization as any;

                    // Normalize plan key
                    let planKey: PlanKey = 'free';
                    if (['starter', 'growth'].includes(rawOrg.plan_key)) {
                        planKey = rawOrg.plan_key as PlanKey;
                    }

                    // Construct clean object
                    const org: OrgWithPlan = {
                        id: rawOrg.id,
                        name: rawOrg.name,

                        logo_path: rawOrg.logo_path || null,
                        plan_key: planKey,
                        plan_status: rawOrg.plan_status || "active",
                        stripe_customer_id: rawOrg.stripe_customer_id || null,
                        stripe_subscription_id: rawOrg.stripe_subscription_id || null,
                        stripe_price_id: rawOrg.stripe_price_id || null,
                        current_period_end: rawOrg.current_period_end || null,
                        trial_end: rawOrg.trial_end || null,
                        cancel_at_period_end: rawOrg.cancel_at_period_end || false,
                        sponsor_name: rawOrg.sponsor_name || null,
                        sponsor_address: rawOrg.sponsor_address || null,
                        support_email: rawOrg.support_email || null,
                    };

                    return { org, membership };
                }

                // Membership exists, but Org is missing/null (RLS or deleted)
                return { org: null, membership };
            }

            // No membership row found (PGRST116 equivalent on maybeSingle with no rows)
            return { org: null, membership: null };
        }
    });

    // 3. Auto-create fallback (Backwards Compatibility)
    useEffect(() => {
        // Only attempt if:
        // 1. Not loading user or org
        // 2. User exists
        // 3. Org query finished but returned NO MEMBERSHIP (membership: null)
        //    (If membership exists but org is null, we do NOT auto-create, to avoid duplication/errors)
        // 4. We haven't tried creating yet (ref)
        // 5. No previous creation error
        if (!isUserLoading && !isOrgLoading && user && orgData && !orgData.membership && !isCreatingRef.current && !createError) {

            isCreatingRef.current = true;

            const attemptCreate = async () => {
                console.log("No organization found for user. Auto-creating default org...");

                // Determine name
                const storedName = localStorage.getItem("companyName");
                const metaName = user.user_metadata?.company_name || user.user_metadata?.companyName;
                const companyName = storedName || metaName || "Default Organization";

                try {
                    const { error } = await supabase.functions.invoke("create-organization", {
                        body: { name: companyName }
                    });

                    if (error) {
                        console.error("Failed to auto-create org:", error);
                        setCreateError(new Error(error.message || "Failed to create organization"));
                        // We do NOT reset isCreatingRef, to prevent loop.
                    } else {
                        console.log("Auto-created org successfully. Refetching...");
                        // Invalidate to force refetch
                        await queryClient.invalidateQueries({ queryKey: ["org-context"] });
                        refetch();
                    }
                } catch (err: any) {
                    console.error("Exception in auto-create org:", err);
                    setCreateError(err instanceof Error ? err : new Error(String(err)));
                }
            };

            attemptCreate();
        }
    }, [user, orgData, isUserLoading, isOrgLoading, queryClient, refetch, createError]);


    const retry = () => {
        setCreateError(null);
        isCreatingRef.current = false; // Allow one more attempt if it was a creation failure
        refetch();
    };

    return {
        user,
        org: orgData?.org || null,
        membership: orgData?.membership || null,
        isLoading: isUserLoading || isOrgLoading,
        error: orgError || createError,
        refetch: retry
    };
}
